/**
 * AI Systems Graph Transformation
 *
 * Transforms agents, flows, and models into graph visualization data
 * with health indicators and relationship mapping
 */

import type { GraphData } from '../types/primitives';
import { aiRegistryClient } from '../services/ai-registry-client';

interface AIAgent {
  id: string;
  name: string;
  description: string;
  domain: string;
  category: string;
  dependencies: string[];
  performance: {
    targetLatency: number;
    maxLatency: number;
    avgLatency?: number;
  };
  healthStatus: {
    status: 'healthy' | 'degraded' | 'down' | 'idle';
    lastCheckAt: any;
    lastExecutionAt?: any;
    errorRate: number;
    totalExecutions: number;
    successfulExecutions: number;
  };
  tags: string[];
}

interface AIFlow {
  id: string;
  name: string;
  type: 'specialist' | 'manager' | 'orchestrator' | 'utility';
  category?: string;
  modelId?: string;
  dependencies: string[];
  healthStatus: {
    status: 'healthy' | 'degraded' | 'down';
    errorRate: number;
    avgLatencyMs: number;
  };
}

interface AIModel {
  id: string;
  name: string;
  provider: string;
  capabilities: {
    streaming: boolean;
    thinking: boolean;
    vision: boolean;
    functionCalling: boolean;
  };
  status: 'active' | 'deprecated' | 'beta';
}

/**
 * Get color for agent based on health status
 */
function getAgentColor(status: AIAgent['healthStatus']['status']): string {
  switch (status) {
    case 'healthy':
      return '#10B981'; // Green
    case 'degraded':
      return '#F59E0B'; // Amber
    case 'down':
      return '#EF4444'; // Red
    case 'idle':
      return '#6B7280'; // Gray
    default:
      return '#6366F1'; // Indigo (default)
  }
}

/**
 * Get color for flow based on health status
 */
function getFlowColor(status: AIFlow['healthStatus']['status']): string {
  switch (status) {
    case 'healthy':
      return '#8B5CF6'; // Violet
    case 'degraded':
      return '#F59E0B'; // Amber
    case 'down':
      return '#EF4444'; // Red
    default:
      return '#8B5CF6'; // Violet (default)
  }
}

/**
 * Get color for model based on status
 */
function getModelColor(status: AIModel['status']): string {
  switch (status) {
    case 'active':
      return '#F59E0B'; // Amber
    case 'deprecated':
      return '#6B7280'; // Gray
    case 'beta':
      return '#3B82F6'; // Blue
    default:
      return '#F59E0B'; // Amber (default)
  }
}

/**
 * Get node size based on usage/importance
 */
function getAgentSize(agent: AIAgent): number {
  if (agent.healthStatus.totalExecutions === 0) return 8;
  if (agent.healthStatus.totalExecutions < 10) return 10;
  if (agent.healthStatus.totalExecutions < 100) return 12;
  return 15;
}

/**
 * Get relationship color based on type
 */
function getRelationshipColor(type: string): string {
  switch (type) {
    case 'executes':
      return '#8B5CF6'; // Violet
    case 'uses':
      return '#3B82F6'; // Blue
    case 'depends_on':
      return '#6B7280'; // Gray
    case 'analyzes':
      return '#EC4899'; // Pink
    case 'generates':
      return '#10B981'; // Green
    default:
      return '#6B7280'; // Gray (default)
  }
}

/**
 * Transform agents, flows, and models into graph data
 */
export function transformAISystemsToGraph(
  agents: AIAgent[],
  flows: AIFlow[],
  models: AIModel[]
): GraphData {
  const nodes: any[] = [];
  const edges: any[] = [];

  // Create agent nodes
  agents.forEach((agent) => {
    nodes.push({
      id: `agent:${agent.id}`,
      type: 'agent',
      label: agent.name,
      color: getAgentColor(agent.healthStatus.status),
      size: getAgentSize(agent),
      properties: {
        ...agent,
        nodeType: 'agent',
        healthStatus: agent.healthStatus.status,
        errorRate: agent.healthStatus.errorRate.toFixed(2) + '%',
        executions: agent.healthStatus.totalExecutions,
        successRate: agent.healthStatus.totalExecutions > 0
          ? ((agent.healthStatus.successfulExecutions / agent.healthStatus.totalExecutions) * 100).toFixed(1) + '%'
          : 'N/A',
        avgLatency: agent.performance.avgLatency
          ? agent.performance.avgLatency.toFixed(0) + 'ms'
          : 'N/A',
        domain: agent.domain,
        category: agent.category,
      },
    });
  });

  // Create flow nodes
  flows.forEach((flow) => {
    nodes.push({
      id: `flow:${flow.id}`,
      type: 'flow',
      label: flow.name,
      color: getFlowColor(flow.healthStatus.status),
      size: 12,
      properties: {
        ...flow,
        nodeType: 'flow',
        healthStatus: flow.healthStatus.status,
        errorRate: flow.healthStatus.errorRate.toFixed(2) + '%',
        avgLatency: flow.healthStatus.avgLatencyMs.toFixed(0) + 'ms',
        flowType: flow.type,
      },
    });
  });

  // Create model nodes
  models.forEach((model) => {
    nodes.push({
      id: `model:${model.id}`,
      type: 'model',
      label: model.name,
      color: getModelColor(model.status),
      size: 14,
      properties: {
        ...model,
        nodeType: 'model',
        provider: model.provider,
        status: model.status,
        streaming: model.capabilities.streaming ? 'Yes' : 'No',
        thinking: model.capabilities.thinking ? 'Yes' : 'No',
        vision: model.capabilities.vision ? 'Yes' : 'No',
      },
    });
  });

  // Create edges from agents to flows (executes relationship)
  agents.forEach((agent) => {
    agent.dependencies.forEach((dep) => {
      // Check if dependency is a flow
      const targetFlow = flows.find((f) => f.id === dep);
      if (targetFlow) {
        edges.push({
          id: `agent-flow-${agent.id}-${dep}`,
          source: `agent:${agent.id}`,
          target: `flow:${dep}`,
          type: 'executes',
          label: 'executes',
          color: getRelationshipColor('executes'),
          metadata: {
            relationshipType: 'executes',
            description: `${agent.name} executes ${targetFlow.name}`,
          },
        });
      }
    });
  });

  // Create edges from flows to models (uses relationship)
  flows.forEach((flow) => {
    if (flow.modelId) {
      const targetModel = models.find((m) => m.id === flow.modelId);
      if (targetModel) {
        edges.push({
          id: `flow-model-${flow.id}-${flow.modelId}`,
          source: `flow:${flow.id}`,
          target: `model:${flow.modelId}`,
          type: 'uses',
          label: 'uses',
          color: getRelationshipColor('uses'),
          metadata: {
            relationshipType: 'uses',
            description: `${flow.name} uses ${targetModel.name}`,
          },
        });
      }
    }
  });

  // Create edges from flows to flows (dependency relationship)
  flows.forEach((flow) => {
    flow.dependencies.forEach((dep) => {
      const targetFlow = flows.find((f) => f.id === dep);
      if (targetFlow) {
        edges.push({
          id: `flow-flow-${flow.id}-${dep}`,
          source: `flow:${flow.id}`,
          target: `flow:${dep}`,
          type: 'depends_on',
          label: 'depends on',
          color: getRelationshipColor('depends_on'),
          metadata: {
            relationshipType: 'depends_on',
            description: `${flow.name} depends on ${targetFlow.name}`,
          },
        });
      }
    });
  });

  return {
    nodes,
    edges,
  };
}

/**
 * Fetch and transform AI systems data
 */
export async function fetchAISystemsGraph(): Promise<GraphData> {
  try {
    // Fetch all data in parallel
    const [agents, flows, models] = await Promise.all([
      aiRegistryClient.getAgents().catch(() => []),
      aiRegistryClient.getFlows().catch(() => []),
      aiRegistryClient.getModels().catch(() => []),
    ]);

    return transformAISystemsToGraph(agents, flows, models);
  } catch (error) {
    console.error('Error fetching AI systems graph:', error);
    return { nodes: [], edges: [] };
  }
}

/**
 * Get health summary statistics
 */
export function getAISystemsHealthSummary(graphData: GraphData): {
  agents: { total: number; healthy: number; degraded: number; down: number; idle: number };
  flows: { total: number; healthy: number; degraded: number; down: number };
  models: { total: number; active: number; deprecated: number; beta: number };
} {
  const agents = graphData.nodes.filter((n) => n.type === 'agent');
  const flows = graphData.nodes.filter((n) => n.type === 'flow');
  const models = graphData.nodes.filter((n) => n.type === 'model');

  return {
    agents: {
      total: agents.length,
      healthy: agents.filter((n) => n.properties['healthStatus'] === 'healthy').length,
      degraded: agents.filter((n) => n.properties['healthStatus'] === 'degraded').length,
      down: agents.filter((n) => n.properties['healthStatus'] === 'down').length,
      idle: agents.filter((n) => n.properties['healthStatus'] === 'idle').length,
    },
    flows: {
      total: flows.length,
      healthy: flows.filter((n) => n.properties['healthStatus'] === 'healthy').length,
      degraded: flows.filter((n) => n.properties['healthStatus'] === 'degraded').length,
      down: flows.filter((n) => n.properties['healthStatus'] === 'down').length,
    },
    models: {
      total: models.length,
      active: models.filter((n) => n.properties['status'] === 'active').length,
      deprecated: models.filter((n) => n.properties['status'] === 'deprecated').length,
      beta: models.filter((n) => n.properties['status'] === 'beta').length,
    },
  };
}
